import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	getCmsPanelRequest,
	getPanels,
	getChildPanels,
	getPanelAncestors,
	getContentBlockItemsByName,
	getContentBlockMixedItem,
} from '../../../CmsActions.js';
import { slugify } from '../../../../../util/text.js';

import HtmlComponent from '../../../../../components/Text/HtmlComponent.js';
import CmsBlockItem from '../../content/CmsBlockItem.js';

// Stable anchor for a content section: explicit `anchor` sub-item if authored,
// otherwise derived from the section title.
function getSectionAnchor(content, title) {
	const anchor = getContentBlockMixedItem(content, 'anchor');
	return (anchor && anchor.text) || slugify((title && title.text) || '');
}

function PanelHelp({ dispatch, panel, language, panels, scrollTo = '' }) {
	const [activeSlug, setActiveSlug] = useState(panel.slug);
	const [hostSlug, setHostSlug] = useState(panel.slug);
	const contentRef = useRef(null);

	// Reset navigation when the host panel changes (slug controlled by parent CmsPanel)
	if(panel.slug !== hostSlug) {
		setHostSlug(panel.slug);
		setActiveSlug(panel.slug);
	}

	// Lazily load the active panel content + relatives (ancestors + children) on display / navigation
	useEffect(() => {
		activeSlug && dispatch(getCmsPanelRequest(activeSlug, language));
	}, [activeSlug, language, dispatch]);

	// Prefer the freshest store copy; fall back to the host panel only when navigating to itself
	const activePanel = panels.find(item => item.slug === activeSlug) || (activeSlug === panel.slug ? panel : null);
	const isReady = !!(activePanel && activePanel.blocks);

	// Scroll to the targeted section once the host panel content is rendered
	useEffect(() => {
		if(!scrollTo || !isReady || activeSlug !== hostSlug || !contentRef.current) {
			return undefined;
		}
		// Wait for the off-canvas open animation + rich text rendering before scrolling
		let highlightTimer;
		const timer = setTimeout(() => {
			const target = contentRef.current && contentRef.current.querySelector(`#${CSS.escape(scrollTo)}`);
			if(target) {
				target.scrollIntoView({ behavior: 'smooth', block: 'start' });
				// Briefly highlight the section to draw the eye to it
				target.classList.remove('help-highlight');
				// eslint-disable-next-line no-unused-expressions
				target.offsetWidth; // force reflow so the animation can replay
				target.classList.add('help-highlight');
				highlightTimer = setTimeout(() => target.classList.remove('help-highlight'), 2600);
			}
		}, 350);
		return () => {
			clearTimeout(timer);
			clearTimeout(highlightTimer);
		};
	}, [scrollTo, isReady, activeSlug, hostSlug]);

	// The clicked panel is being fetched (not yet in the store): show a light spinner instead of a blank area
	if(!isReady) {
		return (
			<div className="help-content text-center text-muted py-4">
				<FontAwesomeIcon icon="spinner" spin />
			</div>
		);
	}

	const ancestors = getPanelAncestors(panels, activePanel, language);
	const children = getChildPanels(panels, activeSlug, language);
	const contents = getContentBlockItemsByName(activePanel.blocks, 'content');

	return (
		<div className="help-content" ref={contentRef}>
			{ancestors.length > 0 && (
				<nav aria-label="breadcrumb">
					<ol className="breadcrumb small">
						{ancestors.map(ancestor => (
							<li key={ancestor.slug} className="breadcrumb-item">
								<button type="button" className="btn btn-link p-0 align-baseline text-decoration-none" onClick={() => setActiveSlug(ancestor.slug)}>
									{ancestor.title}
								</button>
							</li>
						))}
						<li className="breadcrumb-item active" aria-current="page">{activePanel.title}</li>
					</ol>
				</nav>
			)}
			{contents.map((content, index) => {
				const title = getContentBlockMixedItem(content, 'title');
				const text = getContentBlockMixedItem(content, 'text');
				const image = getContentBlockMixedItem(content, 'image');
				return (
					<article key={content.id || index} id={getSectionAnchor(content, title)} className={classnames(index > 0 && 'mt-4 pt-4 border-top')}>
						{title && title.text && <h2 className="h5 fw-semibold mb-3">{title.text}</h2>}
						{image && image.path && (
							<figure className="mb-3">
								<CmsBlockItem item={image} type="image" className="img-fluid rounded shadow-sm w-100" />
							</figure>
						)}
						{text && text.text && (
							<div className="text-body-secondary">
								<HtmlComponent text={text.text} />
							</div>
						)}
					</article>
				);
			})}

			{children.length > 0 && (
				<nav className="help-children mt-4 pt-3 border-top">
					<ul className="list-group list-group-flush">
						{children.map(child => (
							<li key={child.slug} className="list-group-item px-0 border-0">
								<button type="button" className="btn btn-link p-0 d-flex align-items-center justify-content-between w-100 text-decoration-none" onClick={() => setActiveSlug(child.slug)}>
									<span>{child.title}</span>
									<FontAwesomeIcon icon="chevron-right" className="text-muted small ms-2" />
								</button>
							</li>
						))}
					</ul>
				</nav>
			)}
		</div>
	);
}

function mapStateToProps(store, props) {
	const language = props.locale || (props.panel || {}).language || store.intl.locale;
	return {
		language,
		panels: getPanels(store, language, 'help'),
	};
}

PanelHelp.propTypes = {
	dispatch: PropTypes.func.isRequired,
	panel: PropTypes.object.isRequired,
	language: PropTypes.string.isRequired,
	panels: PropTypes.arrayOf(PropTypes.object).isRequired,
	scrollTo: PropTypes.string,
};

export default connect(mapStateToProps)(PanelHelp);
